import classNames from 'classnames'
import Button from 'react-bootstrap/Button'
import Dropdown from 'react-bootstrap/Dropdown'
import DropdownButton from 'react-bootstrap/DropdownButton'

import archidektSvg from '@/assets/archidekt.svg'
import Card from '@/components/Card/Card'
import CardHover from '@/components/CardHover/CardHover'
import Icon, { EDHRECIconsType } from '@/components/Icon/Icon'
import OutboundLink from '@/components/OutboundLink/OutboundLink'
import toastService from '@/services/toast.service'
import { useAppSelector } from '@/store/hooks'
import { CommandersPageProps } from '@/types/page'
import { bulkExport, impactLink } from '@/utils/helpers'
import useDirectories from '@/utils/useDirectories'

import styles from './DecklistPanel.module.scss'

type Props = {
  data: CommandersPageProps['data']
}

const CATEGORY_ORDER = [
  'commander',
  'creature',
  'instant',
  'sorcery',
  'artifact',
  'enchantment',
  'battle',
  'planeswalker',
  'land',
]

const CATEGORY_LABEL = {
  Artifact: { plural: 'Artifacts' },
  Battle: { plural: 'Battles' },
  Commander: { plural: 'Commanders' },
  Creature: { plural: 'Creatures' },
  Enchantment: { plural: 'Enchantments' },
  Instant: { plural: 'Instants' },
  Land: { plural: 'Lands' },
  Planeswalker: { plural: 'Planeswalkers' },
  Sorcery: { plural: 'Sorceries' },
}

const generateCategoryLabel = (category: string, num: number, hideSingle: boolean = false) =>
  `${num === 1 ? category : category in CATEGORY_LABEL ? CATEGORY_LABEL[category as keyof typeof CATEGORY_LABEL]?.plural : category}${!hideSingle ? ` (${num})` : ''}`

const DecklistPanel = ({ data: { archidekt, deck, precon_image } }: Props) => {
  const priceSources = useAppSelector((state) => state.user.priceSources)
  const { dir } = useDirectories()

  if (!deck) return null

  let deckList: string[] = []
  let title = dir === 'average-decks' ? 'Average' : ''
  if (Array.isArray(deck)) {
    deckList = deck
  } else {
    deckList = Object.keys(deck.cards).reduce<string[]>(
      (acc, card) => {
        deck.cards[card].forEach((card) => acc.push(`${card?.[1]} ${card?.[0]}`))
        return acc
      },
      [`1 ${deck.commander}`],
    )
    title = deck?.name || ''
  }

  const copyToClipboard = () => {
    navigator.clipboard.writeText(deckList.join('\r\n'))
    toastService.createToast('Copied to clipboard!')
  }

  const archidektDeckbuilderUrl =
    archidekt && `https://archidekt.com/sandbox?deck=${encodeURI(JSON.stringify(archidekt))}`

  const archidektPlaytesterUrl =
    archidekt && `https://archidekt.com/playtester-v2/sandbox?deck=${encodeURI(JSON.stringify(archidekt))}`

  const moxfieldUrl = deck && `https://www.moxfield.com/import?c=${encodeURI(deckList.join('\n'))}`

  const medium = dir === 'average-decks' ? 'avgdeck' : dir

  let cardkingdomUrl = ''
  if (dir === 'precon') {
    const cardkingdomQueries = new URLSearchParams({
      'filter[name]': title,
      'filter[tab]': 'product',
      partner: 'edhrec',
      partner_args: `edhrec,${medium}`,
      utm_campaign: 'edhrec',
      utm_medium: medium,
      utm_source: 'edhrec',
    })
    cardkingdomUrl = 'https://www.cardkingdom.com/catalog/search?' + cardkingdomQueries.toString()
  } else {
    cardkingdomUrl = bulkExport({
      deckLines: deckList,
      medium,
      vendor: 'cardkingdom',
    })
  }

  let manapoolUrl = ''
  if (dir === 'precon') {
    manapoolUrl = 'https://manapool.com/browse_sealed?q=' + encodeURIComponent(title)
  } else {
    manapoolUrl = bulkExport({
      deckLines: deckList,
      medium: 'decklist',
      vendor: 'manapool',
    })
  }

  let tcgplayerUrl = ''
  if (dir === 'precon') {
    const tcgpParams = new URLSearchParams({
      partner: 'EDHREC',
      ProductTypeName: 'Sealed Products',
      q: title,
    })
    tcgplayerUrl = impactLink({
      partner: 'tcgplayer',
      subId1: 'edhrec,decklist',
      u: 'https://www.tcgplayer.com/search/all/product?' + tcgpParams.toString(),
    })
  } else {
    tcgplayerUrl = bulkExport({
      deckLines: deckList,
      medium: 'decklist',
      vendor: 'tcgplayer',
    })
  }

  let scgUrl = ''
  if (dir === 'precon') {
    scgUrl = `https://starcitygames.com/search/?sealed_type=Commander%20Decks&search_query=${encodeURIComponent(title).replace(/\+/g, '%20')}&sort=score`
  }

  return (
    <div className='p-3'>
      <div className='d-flex justify-content-between mb-3 w-100'>
        <h3 className='me-2'>{title} Decklist</h3>
        <DropdownButton size='sm' title='Export' variant='secondary'>
          <Dropdown.Item href={archidektDeckbuilderUrl} target='_blank'>
            Archidekt
          </Dropdown.Item>
          <Dropdown.Item href={moxfieldUrl} target='_blank'>
            Moxfield
          </Dropdown.Item>
          <Dropdown.Item onClick={copyToClipboard} target='_blank'>
            Plaintext
          </Dropdown.Item>
        </DropdownButton>
      </div>
      <div className={styles.decklist}>
        <div className='justify-content-center w-100 gap-4 d-md-flex'>
          <div className={styles.sidebar}>
            <div className={styles.sidebarContent}>
              {precon_image ? (
                <div className={styles.imageWrapper}>
                  <img alt={`Image of the ${title} sealed product`} src={precon_image} />
                </div>
              ) : !Array.isArray(deck) ? (
                deck.commander.map((commander) => (
                  <div className='mb-2' key={commander}>
                    <Card name={commander} />
                  </div>
                ))
              ) : (
                <div className='mb-2'>
                  <Card name={deck?.[0]?.split(' ')?.slice(1)?.join(' ')} />
                </div>
              )}
              <div className={styles.buyLinks}>
                {priceSources?.map((source, i) => {
                  const buyLinkConfig: Record<string, { icon: EDHRECIconsType; label: string; url: string }> = {
                    cardkingdom: { icon: 'cardkingdom', label: 'Buy at Card Kingdom', url: cardkingdomUrl },
                    manapool: { icon: 'manapool', label: 'Buy at Manapool', url: manapoolUrl },
                    scg: { icon: 'scg', label: 'Buy at Star City Games', url: scgUrl },
                    tcgplayer: { icon: 'tcgplayer', label: 'Buy at TCGPlayer', url: tcgplayerUrl },
                  }

                  const config = buyLinkConfig[source]
                  if (!config || !config.url) return null

                  return (
                    <OutboundLink className={styles.buyLink} key={`${i}-${source}`} url={config.url}>
                      <div className='d-flex flex-row align-items-center mb-1'>
                        <Icon icon={config.icon} />
                        <span className='ms-1'>{config.label}</span>
                      </div>
                    </OutboundLink>
                  )
                })}
              </div>
              <div className='d-grid'>
                <Button className={styles.archidekt} href={archidektPlaytesterUrl} target='_blank'>
                  <img alt='Archidekt' src={archidektSvg.src} />
                  <span>Playtest on Archidekt</span>
                </Button>
              </div>
            </div>
          </div>
          <ul className={classNames([styles.deckGroups, Array.isArray(deck) ? styles.noMargin : ''])}>
            {Array.isArray(deck) ? (
              <li className={styles.breakable}>
                <ul>
                  {deck.map((card) => (
                    <li key={card}>
                      <strong>{card.substring(0, card.indexOf(' '))}</strong>{' '}
                      <span>
                        <CardHover name={card.substring(card.indexOf(' ') + 1)} />
                      </span>
                    </li>
                  ))}
                </ul>
              </li>
            ) : (
              <>
                <li>
                  <h3 className='mb-2'>{generateCategoryLabel('Commander', deck.commander.length, true)}</h3>
                  <ul>
                    {deck.commander.map((commander) => (
                      <li key={commander}>
                        <strong>1</strong>
                        <CardHover name={commander} />
                      </li>
                    ))}
                  </ul>
                </li>
                {Object.keys(deck.cards)
                  .sort((a, b) => CATEGORY_ORDER.indexOf(a.toLowerCase()) - CATEGORY_ORDER.indexOf(b.toLowerCase()))
                  .map((category) => (
                    <li key={category}>
                      <h3 className='mb-2'>
                        {generateCategoryLabel(
                          category,
                          deck.cards[category].reduce((acc, card) => acc + card[1], 0),
                        )}
                      </h3>
                      <ul>
                        {deck.cards[category].map((card) => (
                          <li key={card[0]}>
                            <strong>{card[1] || 1}</strong>
                            <span>
                              <CardHover name={card[0]} />
                            </span>
                          </li>
                        ))}
                      </ul>
                    </li>
                  ))}
              </>
            )}
          </ul>
        </div>
      </div>
    </div>
  )
}

export default DecklistPanel
